import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import OTPInput from '../common/input/OTPInput/OTPInput';
import styles from './VendorAccessModal.module.css';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import { useDispatch, useSelector } from 'react-redux';
import pineappleLogo from '../../images/core/pineapple-logo-black-text.svg';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    getVendorAppointmentsByAccessCode,
    clearVendorAppointmentsState,
} from '../../redux/vendorAppointments/actions/vendorAppointments.action';

interface VendorAccessFormData {
    accessCode: string;
    otp: string[];
}

const VendorAccessModal = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const dispatch: AppDispatch = useDispatch();
    const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
    const [isLoading, setIsLoading] = useState(false);
    const [showNoAppointment, setShowNoAppointment] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const { data: locationData } = useSelector((state: IRootState) => state.locationReducer.locationPublicDetails);
    const {
        data: vendorAppointmentsData,
        loading: vendorAppointmentsLoading,
        error: vendorAppointmentsError,
    } = useSelector((state: IRootState) => state.vendorAppointmentsReducer.vendorAppointments);
    const { handleSubmit } = useForm<VendorAccessFormData>({
        defaultValues: {
            otp: Array(6).fill(''),
        },
        mode: 'onChange',
    });

    // Handle API response - only after user has submitted
    useEffect(() => {
        try {
            if (hasSubmitted && !vendorAppointmentsLoading) {
                const appointmentId = vendorAppointmentsData?.appointmentId;
                if (appointmentId) {
                    navigate(`/vendor-appointment/${appointmentId}`);
                } else if (!appointmentId && !vendorAppointmentsError) {
                    setIsLoading(false);
                    setShowNoAppointment(true);
                } else if (hasSubmitted && !vendorAppointmentsLoading && vendorAppointmentsError) {
                    toast.error(vendorAppointmentsError || 'Failed to fetch vendor appointments. Please try again.');
                    setIsLoading(false);
                }

                setHasSubmitted(false);
            }
        } catch (error) {
            toast.error('An unexpected error occurred. Please try again.');
            setIsLoading(false);
        } finally {
            setIsLoading(false);
        }
    }, [hasSubmitted, vendorAppointmentsLoading, vendorAppointmentsData, vendorAppointmentsError, navigate]);

    // Clear state when component unmounts
    useEffect(() => {
        localStorage.removeItem('vendorAccessToken');

        return () => {
            dispatch(clearVendorAppointmentsState());
        };
    }, []);

    const onSubmit = async (data: VendorAccessFormData) => {
        if (!id) {
            navigate(-1);
            return;
        }
        setIsLoading(true);
        setShowNoAppointment(false);
        setHasSubmitted(true);

        try {
            // Convert OTP array to access code string
            const accessCode = otp.join('');
            dispatch(
                getVendorAppointmentsByAccessCode({
                    accessCode,
                    locationId: id,
                }),
            );
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Failed to submit access code. Please try again.');
            setIsLoading(false);
        }
    };

    const isFormValid = otp.every((digit) => digit !== '');

    // No vendor appointment found UI
    if (showNoAppointment) {
        return (
            <div className={styles.modalContainer}>
                <div className={styles.modalContent}>
                    {/* Header */}
                    {/* <div className={styles.modalHeader}>
            <div className={styles.logoContainer}>
              <img
                src={pineappleLogo}
                alt="Pineapple Logo"
                className={styles.logo}
              />
            </div>
          </div> */}
                    <div className={styles.modalHeader}>
                        <div className={styles.logoContainer}>
                            <img src={pineappleLogo} alt="Pineapple Logo" className={styles.logo} />
                        </div>
                    </div>

                    {/* No Appointment Content */}
                    <div className={styles.noAppointmentContent}>
                        <div className={styles.noAppointmentIcon}>
                            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                                    fill="#8c8d90"
                                />
                            </svg>
                        </div>
                        <h2 className={styles.noAppointmentTitle}>No Vendor Appointments Found</h2>
                        <p className={styles.noAppointmentText}>
                            There are currently no vendor appointments scheduled for this location. Please contact the office for more
                            information.
                        </p>

                        <div className={styles.buttonGroup}>
                            <button
                                type="button"
                                className={styles.submitButton}
                                onClick={() => {
                                    setShowNoAppointment(false);
                                    setOtp(Array(6).fill(''));
                                    setHasSubmitted(false);
                                    dispatch(clearVendorAppointmentsState());
                                }}>
                                Try Again
                            </button>

                            <button type="button" className={styles.backButton} onClick={() => navigate(-1)}>
                                Back
                            </button>
                        </div>

                        {/* Contact Office Link */}
                        <div className={styles.contactSection}>
                            <p className={styles.contactText}>
                                Need help?
                                <a
                                    className={styles.contactButton}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`tel:+${locationData?.data?.leasingAgentNumber || `18887511211p800`}`}>
                                    Contact Office
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.modalContainer}>
            <div className={styles.modalContent}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.logoContainer}>
                        <img src={pineappleLogo} alt="Pineapple Logo" className={styles.logo} />
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                    {/* OTP Input */}
                    <div className={styles.inputGroup}>
                        <h2 className={styles.modalTitle}>Vendor Access</h2>
                        <div className={styles.otpContainer}>
                            <OTPInput otp={otp} setOtp={setOtp} responsive={true} disabled={vendorAppointmentsLoading} />
                        </div>
                        <p className={styles.otpHelperText}>Enter the 6-digit access code</p>
                    </div>

                    {/* Submit Button */}
                    <div className={styles.buttonGroup}>
                        <button
                            type="submit"
                            className={`${styles.submitButton} ${
                                !isFormValid || isLoading || vendorAppointmentsLoading ? styles.submitButtonDisabled : ''
                            }`}
                            disabled={!isFormValid || isLoading || vendorAppointmentsLoading}>
                            {isLoading || vendorAppointmentsLoading ? <div className={styles.loadingSpinner}></div> : 'Submit'}
                        </button>

                        <button
                            type="button"
                            className={`${styles.backButton} ${isLoading ? styles.backButtonDisabled : ''}`}
                            disabled={isLoading}
                            onClick={() => navigate(-1)}>
                            Back
                        </button>
                    </div>

                    {/* Contact Office Link */}
                    <div className={styles.contactSection}>
                        <p className={styles.contactText}>
                            Need help?
                            <a
                                className={styles.contactButton}
                                target="_blank"
                                rel="noopener noreferrer"
                                href={`tel:+${locationData?.data?.leasingAgentNumber || `18887511211p800`}`}>
                                Contact Office
                            </a>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VendorAccessModal;
